<template>
  <v-card class="profile-card-info pa-5">
    <h2>{{ $t(`profile.accountDetails`) }}</h2>
    
    <div v-if="!formReady" class="loading-indicator">
      <v-progress-circular indeterminate></v-progress-circular>
    </div>
    
    <template v-else>
      <v-row class="profile-forms-row" dense>
        <v-col
          v-for="(field, index) in formFields"
          :key="index"
          cols="6"
          class="profile-form-field"
        >
          <!-- Поле ФИО (только чтение) -->
          <v-text-field
            v-if="field.model === 'name'"
            :label="$t(`profile.${field.model}`)"
            outlined
            readonly
            disabled
            v-model="localForm[field.model]"
          />
          
          <!-- Поле телефона -->
          <v-text-field
            v-else-if="field.model === 'phone'"
            :label="$t(`profile.${field.model}`)"
            v-mask="'+7(###) ###-##-##'"
            outlined
            :disabled="field.disabled"
            v-model="localForm[field.model]"
            :error="$v.localForm[field.model].$error"
            :error-messages="getValidationError(field.model)"
            @blur="$v.localForm[field.model].$touch()"
            @input="updateParentForm"
          />
          
          <!-- Дополнительный телефон (необязательный) -->
          <v-text-field
            v-else-if="field.model === 'additionalPhone'"
            :label="$t(`profile.${field.model}`) + ' ' + $t(`validation.notNecessary`)"
            v-mask="'+7(###) ###-##-##'"
            outlined
            :disabled="field.disabled"
            v-model="localForm[field.model]"
            :error="$v.localForm[field.model].$error"
            :error-messages="getValidationError(field.model)"
            @blur="$v.localForm[field.model].$touch()"
            @input="updateParentForm"
            persistent-hint
          />
          
          <!-- Поле даты выдачи -->
          <v-menu
            v-else-if="field.model === 'issueDate'"
            v-model="dateMenu"
            :close-on-content-click="false"
            transition="scale-transition"
            offset-y
            min-width="auto"
          >
            <template v-slot:activator="{ on, attrs }">
              <v-text-field
                v-model="localForm.issueDate"
                :label="$t(`profile.${field.model}`)"
                readonly
                outlined
                v-bind="attrs"
                v-on="on"
                :error="$v.localForm.issueDate.$error"
                :error-messages="getValidationError('issueDate')"
                @blur="$v.localForm.issueDate.$touch()"
              />
            </template>
            <v-date-picker
              v-model="localForm.issueDate"
              no-title
              scrollable
              :max="maxDate"
              :locale="$i18n.locale"
              @input="dateMenu = false; updateParentForm()"
            />
          </v-menu>
          
          <!-- Поле номера документа -->
          <v-text-field
            v-else-if="field.model === 'docNumber'"
            :label="$t(`profile.${field.model}`)"
            outlined
            :disabled="field.disabled"
            v-model="localForm[field.model]"
            :counter="9"
            maxlength="9"
            :error="$v.localForm[field.model].$error"
            :error-messages="getValidationError(field.model)"
            @blur="$v.localForm[field.model].$touch()"
            @input="limitDocNumberLength"
            persistent-hint
          />
          
          <!-- Поле email -->
          <v-text-field
            v-else-if="field.model === 'email'"
            :label="$t(`profile.${field.model}`)"
            outlined
            :disabled="field.disabled"
            v-model="localForm[field.model]"
            :error="$v.localForm[field.model].$error"
            :error-messages="getValidationError(field.model)"
            @blur="$v.localForm[field.model].$touch(); validateEmail()"
            @input="updateParentForm"
          />
          
          <!-- Select поля -->
          <v-select
            v-else-if="field.model === 'docType' || field.model === 'issuedBy'"
            v-model="localForm[field.model]"
            :items="getSelectItems(field.model)"
            item-text="name"
            item-value="name"
            :label="$t(`profile.${field.model}`)"
            outlined
            :disabled="field.disabled"
            :error="$v.localForm[field.model].$error"
            :error-messages="getValidationError(field.model)"
            @blur="$v.localForm[field.model].$touch()"
            @change="updateParentForm"
          />
          
          <!-- Остальные поля -->
          <v-text-field
            v-else
            :label="$t(`profile.${field.model}`)"
            outlined
            :disabled="field.disabled"
            v-model="localForm[field.model]"
            :error="$v.localForm[field.model].$error"
            :error-messages="getValidationError(field.model)"
            @blur="$v.localForm[field.model].$touch()"
            @input="updateParentForm"
          />
        </v-col>
      </v-row>
      
      <!-- Кнопки -->
      <div class="profile-actions">
        <v-checkbox
            v-model="localAgreement"
            :error="!localAgreement && touched"
            @change="handleAgreementChange"
        >
          <template v-slot:label>
            <div class="checkbox-label">
              {{ $t('registration.firstStep.iAgree') }}
              <a 
                href="#" 
                class="agreement-link"
                @click.stop.prevent="openAgreementModal"
              >
                {{ $t('registration.firstStep.processingData') }}
              </a>
            </div>
          </template>
        </v-checkbox>
        <div class="btn-box">
          <v-btn
            color="white"
            outlined
            class="profile-save-btn"
            @click="handleSubmit"
            :disabled="isSubmitLoading || loading"
            :loading="isSubmitLoading"
          >
            {{ $t(`profile.saveButton`) }}
          </v-btn>
          <v-btn
            class="profile-exit-btn"
            outlined
            @click="logOut"
            :disabled="isSubmitLoading || loading"
            :loading="isSubmitLoading"
            v-if="$route.path == '/registration'"
            >
            {{ $t("landing.exitBtn") }}
          </v-btn>
        </div>
      </div>
    </template>
  </v-card>
</template>

<script>
import { validationMixin } from "vuelidate";
import { email, helpers, required } from "vuelidate/lib/validators";

const phoneMask = helpers.regex(
  "phoneMask",
  /^\+7\(\d{3}\)\s\d{3}-\d{2}-\d{2}$/
);

const docNumberValidator = helpers.regex(
  "docNumberValidator",
  /^\d{9}$/
);

const notFutureDate = (value) => {
  if (!value) return true;
  const selectedDate = new Date(value);
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  return selectedDate <= today;
};

export default {
  name: "ProfileForm",
  mixins: [validationMixin],
  
  props: {
    formData: {
      type: Object,
      default: () => ({
        name: "",
        docType: "",
        email: "",
        docNumber: "",
        workplace: "",
        issuedBy: "",
        phone: "",
        issueDate: "",
        additionalPhone: "",
      })
    },
    docTypeItems: {
      type: Array,
      default: () => []
    },
    issuedByItems: {
      type: Array,
      default: () => []
    },
    loading: {
      type: Boolean,
      default: false
    },
    isSubmitLoading: {
      type: Boolean,
      default: false
    }
  },
  
  data() {
    return {
      dateMenu: false,
      maxDate: new Date().toISOString().substr(0, 10),
      touched: false,
      localAgreement: false,
      localForm: {},
      formReady: false,
      
      formFields: [
        { model: "name", disabled: true },
        { model: "docType", disabled: false },
        { model: "email", disabled: false },
        { model: "docNumber", disabled: false },
        { model: "workplace", disabled: false },
        { model: "issuedBy", disabled: false },
        { model: "phone", disabled: false },
        { model: "issueDate", disabled: false },
        { model: "additionalPhone", disabled: false },
      ],
    };
  },
  
  validations() {
    return {
      localForm: {
        name: { required },
        docType: { required },
        email: { required, email },
        docNumber: { required, docNumberValidator },
        workplace: { required },
        issuedBy: { required },
        phone: { required, phoneMask },
        issueDate: { required, notFutureDate },
        additionalPhone: { phoneMask },
      }
    };
  },
  
  created() {
    this.initializeForm();
  },
  
  watch: {
    formData: {
      handler(newVal) {
        this.initializeForm();
      },
      deep: true,
      immediate: false
    }
  },
  
  methods: {
    initializeForm() {
      const defaultForm = {
        name: "",
        docType: "",
        email: "",
        docNumber: "",
        workplace: "",
        issuedBy: "",
        phone: "",
        issueDate: "",
        additionalPhone: "",
      };
      
      this.localForm = { ...defaultForm, ...this.formData };
      this.formReady = true;
    },
    
    getSelectItems(model) {
      if (model === "docType") return this.docTypeItems || [];
      if (model === "issuedBy") return this.issuedByItems || [];
      return [];
    },
    
    limitDocNumberLength() {
      if (!this.localForm.docNumber) return;
      
      const digitsOnly = this.localForm.docNumber.replace(/\D/g, '');
      
      this.localForm.docNumber = digitsOnly.slice(0, 9);
      
      if (digitsOnly.length === 8) {
        this.$v.localForm.docNumber.$touch();
      }
      this.updateParentForm();
    },
    
    validateEmail() {
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      if (this.localForm.email && !emailRegex.test(this.localForm.email)) {
        this.$v.localForm.email.$touch();
      }
      this.updateParentForm();
    },
    
    getValidationError(field) {
      const input = this.$v.localForm[field];
      if (!input || !input.$dirty) return [];
      
      const messages = [];

      if (field === 'additionalPhone' && (!this.localForm[field] || !this.localForm[field].trim())) {
        return [];
      }

      if (input.required === false) messages.push(this.$t("validation.required"));
      if (input.email === false) messages.push(this.$t("validation.email"));
      if (input.docNumberValidator === false) messages.push(this.$t("validation.maximum9"));
      if (input.phoneMask === false) messages.push(this.$t("validation.phone"));
      if (input.notFutureDate === false) messages.push(this.$t("validation.futureDate"));

      return messages;
    },
    
    updateParentForm() {
      this.$nextTick(() => {
        this.$emit('update:formData', { ...this.localForm });
      });
    },
    
    handleAgreementChange() {
      this.touched = true;
    },
    
    handleSubmit() {
      this.$v.localForm.$touch();
      this.touched = true;
      
      if (this.$v.localForm.$invalid || !this.localAgreement) {
        this.$emit('validation-error', {
          invalid: this.$v.localForm.$invalid,
          agreement: !this.localAgreement
        });
        return;
      }
      
      const requiredFields = ['name', 'docType', 'email', 'docNumber', 'workplace', 'issuedBy', 'phone', 'issueDate'];
      const hasEmptyRequiredFields = requiredFields.some(field => 
        !this.localForm[field] || this.localForm[field].toString().trim() === ''
      );
      
      if (hasEmptyRequiredFields) {
        this.$emit('validation-error', {
          emptyFields: requiredFields.filter(field => 
            !this.localForm[field] || this.localForm[field].toString().trim() === ''
          )
        });
        return;
      }
      
      const docTypeItem = this.docTypeItems.find(item => item.name === this.localForm.docType);
      const issuedByItem = this.issuedByItems.find(item => item.name === this.localForm.issuedBy);
      
      const submissionData = {
        ...this.localForm,
        docType: docTypeItem?.id || this.localForm.docType,
        issuedBy: issuedByItem?.id || this.localForm.issuedBy,
        agreement: this.localAgreement
      };
      
      this.$emit('submit', submissionData);
    },

    logOut() {
      this.isLoading = true;
      this.$store.commit("auth/LOGOUT");

      setTimeout(() => {
        window.location.reload();
      }, 500);
    },

    openAgreementModal() {
      window.open('/privacyPolicy', '_blank');
    }
  }
};
</script>

<style scoped>
.profile-card-info {
  width: 950px;
  padding: 40px !important;
}

.profile-form-field {
  margin-bottom: 10px;
}

.profile-actions {
  display: flex;
  justify-content: space-around;
  align-items: center;
  width: 100%;
}

.profile-save-btn {
  background-color: #5e5af9 !important;
  color: #ffffff !important;
  border-radius: 8px;
  text-transform: none !important;
  width: 200px;
}

.profile-exit-btn {
  background-color: #ff0000b6 !important;
  color: #ffffff !important;
  border-radius: 8px;
  text-transform: none !important;
  min-width: 120px;
}

.loading-indicator {
  display: flex;
  justify-content: center;
  align-items: center;
  min-height: 300px;
}

.btn-box {
  display: flex;
  gap: 20px;
  width: 50%;
  justify-content: end;
}

@media (max-width: 1024px) {
  .profile-card-info {
    width: 100%;
    padding: 30px !important;
  }
}

@media (max-width: 768px) {
  .profile-card-info {
    padding: 20px !important;
  }
  
  .profile-forms-row {
    flex-direction: column;
  }
  
  .profile-form-field {
    max-width: 100%;
  }
  
  .profile-actions {
    flex-direction: column;
    justify-content: center;
    align-items: center;
    gap: 10px;
  }

  .btn-box{
    width: 100%;
    flex-direction: column;
    gap: 5px;
    align-items: center;
  }
  
  .profile-save-btn {
    width: 80%;
    padding: 25px 20px;
    margin-top: 20px;
  }

  .profile-exit-btn {
    width: 50%;
    padding: 25px 20px;
    margin-top: 20px;
  }
}

@media (max-width: 480px) {
}
</style>